import { useEffect, useRef } from "react";

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    setTimeout(() => el.classList.add("hero-visible"), 100);
  }, []);

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="inicio"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background image — desktop */}
      <img
        src="https://storage.readdy-site.link/project_files/9309375e-f328-4fba-b8d7-64d73f28a855/9643d223-d0e5-4b5e-bb22-615dcc800df4_compressed_WhatsApp-Image-2026-07-14-at-15.22.10.webp"
        alt=""
        aria-hidden="true"
        className="hidden md:block absolute inset-0 w-full h-full object-cover object-[position:center_-95%]"
      />

      {/* Background image — mobile */}
      <img
        src="https://storage.readdy-site.link/project_files/9309375e-f328-4fba-b8d7-64d73f28a855/0c8088ab-59c7-4eff-adf1-0c22449d5cec_compressed_WhatsApp-Image-2026-07-14-at-13.54.05.webp"
        alt=""
        aria-hidden="true"
        className="md:hidden absolute inset-0 w-full h-full object-cover object-[position:center_-95%]"
      />

      {/* Bottom gradient to blend with next section */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white via-white/60 to-transparent z-[1]" />

      {/* Content */}
      <div
        ref={heroRef}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-28 pb-16 hero-content"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            {/* Label */}
            <div className="inline-flex items-center gap-2 mb-6 opacity-0 animate-fade-up" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>
              <span className="w-2 h-2 rounded-full bg-black animate-pulse" />
              <span className="text-black/70 text-xs font-semibold tracking-widest uppercase">
                Aceleradora de Pessoas e Resultados
              </span>
            </div>

            {/* Headline */}
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-black leading-tight mb-4 opacity-0 animate-fade-up"
              style={{ animationDelay: "0.25s", animationFillMode: "forwards" }}
            >
              O Futuro da sua empresa{" "}
              <span className="relative inline-block">
                <span className="relative z-10">começa aqui.</span>
                <span className="absolute bottom-1 left-0 right-0 h-3 bg-black/10 -z-0 rounded" />
              </span>
            </h1>

            {/* Subtitle */}
            <p
              className="text-xl md:text-2xl text-black font-semibold leading-snug mb-4 max-w-xl opacity-0 animate-fade-up"
              style={{ animationDelay: "0.35s", animationFillMode: "forwards" }}
            >
              Todos os benefícios e seguros em um só lugar.
            </p>

            {/* Subheadline */}
            <p
              className="text-lg md:text-xl text-black/70 leading-relaxed mb-8 max-w-xl opacity-0 animate-fade-up"
              style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}
            >
              A Ferrarezi não entrega apenas benefícios e seguros. Nós entregamos <strong className="text-black">estratégia para fortalecer pessoas e acelerar resultados</strong>. Redução de custos, controle de sinistralidade, retenção de talentos e produtividade real.
            </p>

            {/* CTAs */}
            <div
              className="w-full sm:w-auto flex flex-col sm:flex-row gap-4 items-stretch opacity-0 animate-fade-up mb-8"
              style={{ animationDelay: "0.5s", animationFillMode: "forwards" }}
            >
              <button
                onClick={() => scrollTo("#contato")}
                className="whitespace-nowrap bg-[#FBC52D] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#e5b41c] transition-all duration-200 cursor-pointer text-sm"
              >
                Fale com nossos especialistas
              </button>
              <button
                onClick={() => scrollTo("#resultados")}
                className="whitespace-nowrap border-2 border-black/40 text-black font-semibold px-8 py-4 rounded-full hover:bg-black/10 hover:border-black/70 transition-all duration-200 cursor-pointer text-sm"
              >
                Ver resultados que entregamos
              </button>
            </div>
          </div>

          {/* Right: Avatar IA / Video Hero, visible on mobile too */}
          <div
            className="flex flex-col items-center justify-center opacity-0 animate-fade-up"
            style={{ animationDelay: "0.5s", animationFillMode: "forwards" }}
          >
            <div className="relative w-full max-w-sm lg:max-w-md">
              {/* Glow ring */}
              <div className="absolute inset-0 rounded-2xl bg-black/10 blur-2xl scale-110" />

              {/* Video/Avatar container */}
              <div className="relative rounded-2xl overflow-hidden border-2 border-black/15 bg-black/10 backdrop-blur-sm aspect-[4/5]">
                <img
                  src="https://storage.readdy-site.link/project_files/9309375e-f328-4fba-b8d7-64d73f28a855/f1bf55c5-5354-452d-afd7-181f5a09278e_Design-sem-nome-1.png?v=cebfd1cbce9d6fdf5c5b8d887362ac96"
                  alt="Avatar IA Ferrarezi"
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                {/* Overlay badge */}
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="bg-black/80 backdrop-blur-md border border-[#FBC52D]/40 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#FBC52D] animate-pulse" />
                      <span className="text-white text-xs font-semibold">Ferrarezi Intelligence, IA Ativa</span>
                    </div>
                    <p className="text-white/60 text-xs mt-1.5 leading-relaxed">
                      Painel executivo de saúde e riscos em tempo real
                    </p>
                  </div>
                </div>

                {/* Corner tech decorations */}
                <div className="absolute top-4 right-4 flex flex-col gap-1.5">
                  <div className="w-8 h-px bg-black/40" />
                  <div className="w-5 h-px bg-black/25" />
                </div>
                <div className="absolute top-4 left-4 flex flex-col gap-1.5">
                  <div className="w-8 h-px bg-black/40" />
                  <div className="w-5 h-px bg-black/25" />
                </div>
              </div>

              {/* Floating tech cards, hidden on mobile to avoid overflow */}
              <div className="hidden lg:block absolute -left-8 top-1/3 bg-black/90 backdrop-blur-md border border-[#FBC52D]/30 rounded-xl p-3 w-36">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-heart-pulse-line text-[#FBC52D] text-sm" />
                  <span className="text-white text-xs font-semibold">Saúde Preventiva</span>
                </div>
                <div className="text-[#FBC52D] text-lg font-bold">94%</div>
                <div className="text-white/50 text-xs">índice de prevenção</div>
              </div>

              <div className="hidden lg:block absolute -right-8 bottom-1/3 bg-black/90 backdrop-blur-md border border-[#FBC52D]/30 rounded-xl p-3 w-36">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-bar-chart-2-line text-[#FBC52D] text-sm" />
                  <span className="text-white text-xs font-semibold">Analytics RH</span>
                </div>
                <div className="text-[#FBC52D] text-lg font-bold">Real-time</div>
                <div className="text-white/50 text-xs">dados ao vivo</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Strip — full width below both columns */}
        <div
          className="w-full mt-16 pt-10 border-t border-gray-200 opacity-0 animate-fade-up"
          style={{ animationDelay: "0.75s", animationFillMode: "forwards" }}
        >
          <div className="bg-[#FBC52D] rounded-2xl px-6 py-7 md:px-10 md:py-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-8 gap-x-6 text-center items-start justify-center">
              {[
                { value: "+10 mil", label: "vidas atendidas hoje" },
                { value: "+80", label: "produtos disponíveis" },
                { value: "+500", label: "empresas atendidas" },
                { value: "+30", label: "anos de experiência" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-2xl md:text-3xl font-bold text-black">{stat.value}</div>
                  <div className="text-black/60 text-sm mt-1 leading-snug">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up {
          animation: fade-up 0.7s ease forwards;
        }
      `}</style>
    </section>
  );
}